import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LuArrowRight,
  LuBookOpen,
  LuBuilding2,
  LuCircleHelp,
  LuClipboardList,
  LuFileWarning,
  LuHeadphones,
  LuMail,
  LuMessageCircle,
  LuPackage,
  LuPhone,
  LuScale,
  LuSearch,
  LuShield,
  LuShoppingCart,
  LuTicket,
  LuTruck,
  LuWallet,
  LuWrench,
} from "react-icons/lu";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import HelpSupportIllustration from "../../components/help/HelpSupportIllustration";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/help_center.scss";

const POPULAR_TAGS = [
  "RFQ",
  "Orders",
  "Seller Verification",
  "Payments",
  "Logistics",
  "Product Upload",
];

const TOPICS = [
  {
    id: "buyer",
    title: "Buyer Support",
    description: "RFQs, quotations, purchase orders",
    icon: LuShoppingCart,
    tone: "blue",
  },
  {
    id: "seller",
    title: "Seller Support",
    description: "Registration, verification, catalog upload",
    icon: LuPackage,
    tone: "green",
  },
  {
    id: "orders-payments",
    title: "Orders & Payments",
    description: "Orders, invoices, refunds, payments",
    icon: LuWallet,
    tone: "yellow",
  },
  {
    id: "contract-manufacturing",
    title: "Contract Manufacturing",
    description: "Manufacturing requests and production",
    icon: LuBuilding2,
    tone: "purple",
  },
  {
    id: "logistics",
    title: "Logistics",
    description: "Shipment booking and tracking",
    icon: LuTruck,
    tone: "cyan",
  },
  {
    id: "technical",
    title: "Technical Support",
    description: "Platform issues and troubleshooting",
    icon: LuWrench,
    tone: "red",
  },
];

const POPULAR_ARTICLES = [
  {
    id: "create-rfq",
    title: "How to Create RFQ",
    description: "Post a sourcing request and invite suppliers.",
    updated: "10 Jul 2026",
    icon: LuClipboardList,
    topic: "buyer",
    tone: "blue",
  },
  {
    id: "upload-products",
    title: "Upload Products",
    description: "Add listings with images, specs, and pricing.",
    updated: "09 Jul 2026",
    icon: LuPackage,
    topic: "seller",
    tone: "green",
  },
  {
    id: "track-orders",
    title: "Track Orders",
    description: "Follow order status from quote to delivery.",
    updated: "08 Jul 2026",
    icon: LuShoppingCart,
    topic: "orders-payments",
    tone: "orange",
  },
  {
    id: "payment-issues",
    title: "Payment Issues",
    description: "Resolve failed payments and invoice problems.",
    updated: "07 Jul 2026",
    icon: LuWallet,
    topic: "orders-payments",
    tone: "purple",
  },
];

const SUPPORT_OPTIONS = [
  {
    id: "chat",
    title: "Live Chat",
    description: "Chat with our support team.",
    action: "Start Chat",
    icon: LuMessageCircle,
    to: "/contact?enquiry=general",
    tone: "blue",
  },
  {
    id: "ticket",
    title: "Raise Support Ticket",
    description: "Create a support request.",
    action: "Raise Ticket",
    icon: LuTicket,
    to: "/help-center/raise-ticket",
    tone: "green",
  },
  {
    id: "email",
    title: "Email Support",
    description: "support@trocanet.com",
    action: "Email Us",
    icon: LuMail,
    to: "mailto:support@trocanet.com",
    tone: "orange",
  },
  {
    id: "call",
    title: "Call Support",
    description: "Business Hours Support",
    action: "Contact Support",
    icon: LuPhone,
    to: "/contact?enquiry=general",
    tone: "purple",
  },
];

const QUICK_LINKS = [
  { title: "Buyer Guide", to: "/buyer-rfq-form", icon: LuBookOpen },
  { title: "Seller Guide", to: "/signup?tradeRole=Seller", icon: LuPackage },
  { title: "Submit Dispute", to: "/contact?enquiry=issue", icon: LuFileWarning },
  { title: "FAQ", to: "/help-center/articles", icon: LuCircleHelp },
  { title: "Terms & Conditions", to: "/terms-and-conditions", icon: LuScale },
  { title: "Privacy Policy", to: "/privacy-policy", icon: LuShield },
];

export default function HelpCenter() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  const filteredArticles = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return POPULAR_ARTICLES;
    return POPULAR_ARTICLES.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q),
    );
  }, [query]);

  const onSearchSubmit = (event) => {
    event.preventDefault();
    const q = query.trim();
    navigate(q ? `/help-center/articles?q=${encodeURIComponent(q)}` : "/help-center/articles");
  };

  const applyTag = (tag) => {
    navigate(`/help-center/articles?q=${encodeURIComponent(tag)}`);
  };

  return (
    <div className="help-center-page">
      <B2BHubHomeHeader />

      <main className="help-center-page__main">
        <section className="help-hero" aria-labelledby="help-hero-title">
          <div className="container-fluid help-hero__inner">
            <nav className="help-breadcrumb" aria-label="Breadcrumb">
              <Link to="/home">Home</Link>
              <span aria-hidden>&gt;</span>
              <span aria-current="page">Help Center</span>
            </nav>

            <div className="help-hero__grid">
              <div className="help-hero__copy">
                <h1 id="help-hero-title">How can we help you today?</h1>
                <p>Search our knowledge base, FAQs, and support resources.</p>

                <form className="help-search" onSubmit={onSearchSubmit}>
                  <LuSearch aria-hidden />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search help articles, orders, payments, RFQs..."
                    aria-label="Search Help Center"
                  />
                </form>

                <div className="help-tags" aria-label="Popular searches">
                  <span className="help-tags__label">Popular searches:</span>
                  {POPULAR_TAGS.map((tag) => (
                    <button key={tag} type="button" className="help-tags__chip" onClick={() => applyTag(tag)}>
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              <div className="help-hero__visual" aria-hidden>
                <HelpSupportIllustration />
              </div>
            </div>
          </div>
        </section>

        <div className="container-fluid help-center-page__body">
          <section className="help-section" aria-labelledby="help-topics-title">
            <div className="help-section__head">
              <h2 id="help-topics-title">Browse Help Topics</h2>
            </div>
            <div className="help-topics help-topics--six">
              {TOPICS.map((topic) => {
                const Icon = topic.icon;
                return (
                  <Link key={topic.id} to={`/help-center/topics/${topic.id}`} className="help-topic-card">
                    <span className={`help-topic-card__icon help-tone--${topic.tone}`} aria-hidden>
                      <Icon />
                    </span>
                    <span className="help-topic-card__copy">
                      <strong>{topic.title}</strong>
                      <span>{topic.description}</span>
                    </span>
                    <LuArrowRight className="help-topic-card__arrow" aria-hidden />
                  </Link>
                );
              })}
            </div>
          </section>

          <section className="help-section" aria-labelledby="help-articles-title">
            <div className="help-section__head help-section__head--row">
              <h2 id="help-articles-title">Popular Articles</h2>
              <Link to="/help-center/articles" className="help-section__view-all">
                View All Articles <LuArrowRight aria-hidden />
              </Link>
            </div>
            <div className="help-articles help-articles--four">
              {filteredArticles.map((article) => {
                const Icon = article.icon;
                return (
                  <article key={article.id} className="help-article-card help-article-card--compact">
                    <span className={`help-article-card__icon help-tone--${article.tone}`} aria-hidden>
                      <Icon />
                    </span>
                    <h3>{article.title}</h3>
                    <p>{article.description}</p>
                    <div className="help-article-card__meta">
                      <span>Last Updated: {article.updated}</span>
                      <Link to={`/help-center/articles/${article.id}`} className="help-article-card__link">
                        Read More <LuArrowRight aria-hidden />
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="help-section" aria-labelledby="help-support-title">
            <div className="help-section__head">
              <h2 id="help-support-title">Need More Help?</h2>
            </div>
            <div className="help-support help-support--compact">
              {SUPPORT_OPTIONS.map((card) => {
                const Icon = card.icon;
                const external = card.to.startsWith("mailto:");
                const Comp = external ? "a" : Link;
                const props = external ? { href: card.to } : { to: card.to };
                return (
                  <article key={card.id} className="help-support-card">
                    <span className={`help-support-card__icon help-tone--${card.tone}`} aria-hidden>
                      <Icon />
                    </span>
                    <h3>{card.title}</h3>
                    <p>{card.description}</p>
                    <Comp {...props} className={`help-support-card__btn help-support-card__btn--${card.tone}`}>
                      {card.action}
                      <LuArrowRight aria-hidden />
                    </Comp>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="help-section" aria-labelledby="help-quick-title">
            <div className="help-section__head">
              <h2 id="help-quick-title">Quick Links</h2>
            </div>
            <div className="help-quick-links">
              {QUICK_LINKS.map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.title} to={item.to} className="help-quick-link">
                    <span className="help-quick-link__icon" aria-hidden>
                      <Icon />
                    </span>
                    <strong>{item.title}</strong>
                    <LuArrowRight aria-hidden />
                  </Link>
                );
              })}
            </div>
          </section>

          <section className="help-cta" aria-labelledby="help-cta-title">
            <div className="help-cta__inner">
              <div className="help-cta__message">
                <span className="help-cta__icon" aria-hidden>
                  <LuHeadphones />
                </span>
                <div>
                  <h2 id="help-cta-title">Still need help?</h2>
                  <p>Our support team is here to assist you.</p>
                </div>
              </div>
              <div className="help-cta__actions">
                <Link to="/help-center/raise-ticket" className="help-cta__btn help-cta__btn--primary">
                  Raise Ticket
                </Link>
                <Link to="/contact" className="help-cta__btn help-cta__btn--ghost">
                  Contact Support
                </Link>
              </div>
            </div>
          </section>
        </div>
      </main>

      <HubHomeFooter />
    </div>
  );
}
